import React, { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { BrandThemeProvider, useBrandCSSVariables } from '../../contexts/BrandThemeContext';
import { NotificationProvider, useNotifications } from './contexts/NotificationContext';
import { ConfirmDialogProvider } from './contexts/ConfirmDialogContext';
import { registerNotificationHandler } from './utils/notifications';
import { Sidebar } from './components/Sidebar';
import { Header } from './components/Header';
import { StripeOnboardingBanner } from './components/StripeOnboardingBanner';
import { ProNewsArrival } from './components/ProNewsArrival';
import './styles.scss';

const NotificationIntegration: React.FC = () => {
  const { showNotification } = useNotifications();

  // Effet pur : enregistrer le handler global (autorisé par CONETEXT.md)
  useEffect(() => {
    registerNotificationHandler((type, message) => {
      // Error et warning : 10 secondes, success et info : 5 secondes
      const dismissAfter = type === 'error' || type === 'warning' ? 10000 : 5000;
      showNotification(type, message, true, dismissAfter);
    });
  }, [showNotification]);

  return null;
};

const LayoutContent: React.FC = () => {
  const cssVariables = useBrandCSSVariables();
  const location = useLocation();

  // Pages sans sidebar et header (login, pages d'erreur standalone, paiement public, etc.)
  const pagesWithoutNavigation = ['/login', '/forgot-password', '/reset-password', '/change-password-required', '/403', '/404'];
  const prefixesWithoutNavigation = ['/payment/'];
  const showNavigation = !pagesWithoutNavigation.includes(location.pathname) &&
    !prefixesWithoutNavigation.some(prefix => location.pathname.startsWith(prefix));

  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <NotificationProvider>
      <ConfirmDialogProvider>
        <NotificationIntegration />
        <div className="tw-min-h-screen tw-flex" style={cssVariables}>
          {/* Sidebar - affichée seulement si l'utilisateur est authentifié */}
          {showNavigation && (
            <Sidebar
              isOpen={sidebarOpen}
              onClose={() => setSidebarOpen(false)}
            />
          )}

          {/* Conteneur principal avec header et contenu */}
          <div className={`tw-flex-1 tw-flex tw-flex-col ${showNavigation ? 'md:tw-ml-64' : ''}`}>
            {/* Header fixe en haut (avec banner impersonation si active) */}
            {showNavigation && (
              <Header onMenuToggle={() => setSidebarOpen(prev => !prev)} />
            )}

            {/* Contenu principal avec margin-top dynamique */}
            <main className={`tw-flex-1 tw-bg-gray-50 ${showNavigation ? 'tw-mt-16' : ''}`}>
              {/* Bandeau Stripe onboarding si nécessaire */}
              {showNavigation && <StripeOnboardingBanner />}
              {/* Nouveautés : bandeau + modale d'arrivée (masqués tant qu'un bandeau Stripe est affiché) */}
              {showNavigation && <ProNewsArrival />}
              <div className={showNavigation ? 'tw-px-4 md:tw-px-6 layout-content-wrapper' : ''}>
                <Outlet />
              </div>
            </main>
          </div>
        </div>
      </ConfirmDialogProvider>
    </NotificationProvider>
  );
};

export const Layout: React.FC = () => {
  return (
    <BrandThemeProvider>
      <LayoutContent />
    </BrandThemeProvider>
  );
};


